/**
 * Shared utility for loading robots/objects from XML files.
 * Used by both preset robots loader and object library.
 */
import { MJCFParser, type MJCFParseResult } from "../core/mjcfParser";
import { getSceneState } from "../stores/sceneStore";
import type { Joint, Scene } from "../types/scene";
import type { AllActions } from "../stores/sceneActions/actionTypes";
import { processSceneAssets } from "./scene-loader/assets";
import { loadSceneFromParsedData } from "./scene-loader/parsedData";
import { applyHomeKeyframeToImportedJoints } from "./scene-loader/keyframes";
import { logger } from "./logger";
import type {
  ParsedActuatorMap,
  ParsedBodyMap,
  ParsedMeshMap,
  ParsedTextureRecords,
  SceneFileMap,
  SceneLoaderOptions,
} from "./scene-loader/types";
export type { ParsedTextureRecords, SceneFileMap, SceneLoaderOptions } from "./scene-loader/types";
export { clearFallbackBlobUrls, clearFileHashCache } from "./scene-loader/blobCache";
export { fetchXmlFile, getRootBodyUuid } from "./scene-loader/publicUtils";
export { normalizeParsedActuatorsByUuid } from "./scene-loader/parsedData";

const restoreSceneAfterFailedLoad = (
  sceneActions: AllActions,
  snapshot: Scene | null,
  error: unknown
) => {
  if (!snapshot) return;
  try {
    sceneActions.restoreState(snapshot);
  } catch (restoreError) {
    logger.error("[sceneLoader] Failed to restore previous scene after import failure:", {
      importError: error,
      restoreError,
    });
  }
};

/**
 * Loads a robot/object from a pre-parsed result into the scene.
 * This variant is used when you've already parsed the XML (e.g., object library with mesh UUID remapping).
 */
export async function loadSceneFromParsedResult(
  parsedResult: MJCFParseResult,
  sceneActions: AllActions,
  options: SceneLoaderOptions = {}
) {
  const {
    resetScene = false,
    fileMap = null,
    processAssets = !!fileMap,
    strictAssetValidation = false,
    deferCollisionMeshProcessing = false,
    meshLibraryPathPrefix,
  } = options;
  const previousScene = resetScene ? getSceneState() : null;

  try {
    if (resetScene) {
      sceneActions.resetScene();
    }
    const {
      materials: parsedMaterials,
      joints: parsedJoints,
      bodies: parsedBodiesMap,
      worldBodies: parsedWorldBodies,
      sites: parsedSitesFromParser,
      cameras: parsedCameras,
      lights: parsedLights,
      textures: parsedTextures,
      meshes: parsedMeshes,
      hfields: parsedHFields,
      actuators: parsedActuators,
      tendons: parsedTendons,
      sensors: parsedSensors,
      equalities: parsedEqualities,
      contactPairs: parsedContactPairs,
      contactExcludes: parsedContactExcludes,
      option: parsedOption,
      mjcfCompiler: parsedMjcfCompiler,
      mjcfStatistic: parsedMjcfStatistic,
    } = parsedResult;

    let textureRecords: ParsedTextureRecords = {};
    let backgroundWork: () => Promise<void> = async () => {};
    if (processAssets && fileMap) {
      const assets = await processSceneAssets(
        parsedBodiesMap as unknown as ParsedBodyMap,
        parsedMeshes as unknown as ParsedMeshMap,
        parsedTextures,
        fileMap,
        strictAssetValidation,
        deferCollisionMeshProcessing,
        meshLibraryPathPrefix
      );
      textureRecords = assets.textureRecords;
      backgroundWork = assets.backgroundWork;
    }

    const result = await loadSceneFromParsedData(
      {
        materials: parsedMaterials,
        joints: parsedJoints,
        bodies: parsedBodiesMap as unknown as ParsedBodyMap,
        worldBodies: parsedWorldBodies,
        sites: parsedSitesFromParser,
        cameras: parsedCameras,
        lights: parsedLights,
        textures: parsedTextures,
        meshes: parsedMeshes as unknown as ParsedMeshMap,
        hfields: parsedHFields,
        actuators: parsedActuators as unknown as ParsedActuatorMap,
        tendons: parsedTendons,
        sensors: parsedSensors,
        equalities: parsedEqualities,
        contactPairs: parsedContactPairs,
        contactExcludes: parsedContactExcludes,
        option: parsedOption,
        mjcfCompiler: parsedMjcfCompiler,
        mjcfStatistic: parsedMjcfStatistic,
      },
      sceneActions,
      options,
      textureRecords
    );

    const homeQpos = parsedResult.keyframes?.home;
    const parsedJointsList =
      (result.parsedResult as { importedJoints?: Joint[] }).importedJoints ??
      parsedResult.joints ??
      [];
    applyHomeKeyframeToImportedJoints(homeQpos, parsedJointsList, sceneActions);

    // When deferCollisionMeshProcessing is true, collision mesh hashing runs here so mesh
    // definitions have fingerprints before callers (and React) observe the loaded scene.
    await backgroundWork();
    return result;
  } catch (error) {
    restoreSceneAfterFailedLoad(sceneActions, previousScene, error);
    throw error;
  }
}

/**
 * Loads a robot/object from an XML file into the scene.
 * This is the shared implementation used by both preset robots and object library.
 */
export async function loadSceneFromXml(
  xmlFile: File,
  fileMap: SceneFileMap,
  sceneActions: AllActions,
  options: SceneLoaderOptions = {}
) {
  const {
    resetScene = false,
    processAssets = true,
    strictAssetValidation = false,
    deferCollisionMeshProcessing = false,
    meshLibraryPathPrefix,
  } = options;
  const previousScene = resetScene ? getSceneState() : null;

  try {
    if (resetScene) {
      sceneActions.resetScene();
    }
    const parser = new MJCFParser();
    const parsedResult = await parser.parseFile(xmlFile);
    const {
      materials: parsedMaterials,
      joints: parsedJoints,
      bodies: parsedBodiesMap,
      worldBodies: parsedWorldBodies,
      sites: parsedSitesFromParser,
      cameras: parsedCameras,
      lights: parsedLights,
      textures: parsedTextures,
      meshes: parsedMeshes,
      hfields: parsedHFields,
      actuators: parsedActuators,
      tendons: parsedTendons,
      sensors: parsedSensors,
      equalities: parsedEqualities,
      contactPairs: parsedContactPairs,
      contactExcludes: parsedContactExcludes,
      option: parsedOption,
      mjcfCompiler: parsedMjcfCompiler,
      mjcfStatistic: parsedMjcfStatistic,
    } = parsedResult;

    let textureRecords: ParsedTextureRecords = {};
    let backgroundWork: () => Promise<void> = async () => {};
    if (processAssets) {
      const assets = await processSceneAssets(
        parsedBodiesMap as unknown as ParsedBodyMap,
        parsedMeshes as unknown as ParsedMeshMap,
        parsedTextures,
        fileMap,
        strictAssetValidation,
        deferCollisionMeshProcessing,
        meshLibraryPathPrefix
      );
      textureRecords = assets.textureRecords;
      backgroundWork = assets.backgroundWork;
    }
    const result = await loadSceneFromParsedData(
      {
        materials: parsedMaterials,
        joints: parsedJoints,
        bodies: parsedBodiesMap as unknown as ParsedBodyMap,
        worldBodies: parsedWorldBodies,
        sites: parsedSitesFromParser,
        cameras: parsedCameras,
        lights: parsedLights,
        textures: parsedTextures,
        meshes: parsedMeshes as unknown as ParsedMeshMap,
        hfields: parsedHFields,
        actuators: parsedActuators as unknown as ParsedActuatorMap,
        tendons: parsedTendons,
        sensors: parsedSensors,
        equalities: parsedEqualities,
        contactPairs: parsedContactPairs,
        contactExcludes: parsedContactExcludes,
        option: parsedOption,
        mjcfCompiler: parsedMjcfCompiler,
        mjcfStatistic: parsedMjcfStatistic,
      },
      sceneActions,
      options,
      textureRecords
    );
    await backgroundWork();
    return result;
  } catch (error) {
    restoreSceneAfterFailedLoad(sceneActions, previousScene, error);
    throw error;
  }
}
